import React, { useEffect, useMemo, useState } from 'react';
import { Button, Group, Select, Stack, Text, Textarea } from '@mantine/core';
import { useForm } from '@mantine/form';

import { Request } from '../../../codegen/graphql';
import { useAppConfig } from '../../../hooks/useAppConfig';
import { PriceChangeRequestMetadata } from '../../../types/price-change-request';
import { useApplyQuickAction } from './useApplyQuickAction';
import { useOptionalPricingBatchStaging } from '../batches/PricingBatchStagingContext';
import { queuedRequestLabel } from '../batches/pricingBatchStaging.types';
import { requestComboboxSelectClassNames } from '../components/requestComboboxClassNames';
import {
    QUICK_ACTION_APPROVE_FIELDS_WIDTH_CLASS,
    quickActionFormFieldsBlockClassName,
    quickActionFormFieldsShellClassName,
} from './quickActionFormLayout';

type Props = {
    request: Request;
    onCancel: () => void;
    onSuccess?: () => void;
};

const fieldRadius = 'md' as const;
const compactBtnClass = 'shrink-0 !min-h-11 !w-auto rounded-full px-8';

export function QuickActionDeclineForm({
    request,
    onCancel,
    onSuccess,
}: Props) {
    const { procurementDeclineReasons } = useAppConfig();
    const batchStaging = useOptionalPricingBatchStaging();
    const { submit, loading, resolvedAction } = useApplyQuickAction(
        request,
        'decline',
    );

    const stagedEntry = batchStaging?.getQueuedEntry(request.id);
    const reopeningStagedForm = stagedEntry?.verb === 'decline';
    const defaultReason = procurementDeclineReasons[0] ?? '';

    const form = useForm({
        initialValues: {
            reason: reopeningStagedForm
                ? stagedEntry?.metadata.reason ?? defaultReason
                : defaultReason,
            notes: reopeningStagedForm ? stagedEntry?.notes ?? '' : '',
        },
        validate: {
            reason: value => (!value ? 'Reason is required' : null),
        },
        validateInputOnBlur: true,
    });

    useEffect(() => {
        if (
            reopeningStagedForm ||
            procurementDeclineReasons.length === 0 ||
            form.values.reason
        ) {
            return;
        }
        form.setFieldValue('reason', procurementDeclineReasons[0]);
    }, [reopeningStagedForm, procurementDeclineReasons, form]);

    const [submitting, setSubmitting] = useState(false);

    const reasonData = useMemo(
        () =>
            procurementDeclineReasons.map(reason => ({
                value: reason,
                label: reason,
            })),
        [procurementDeclineReasons],
    );

    const handleSubmit = form.onSubmit(async values => {
        setSubmitting(true);
        const { reason, notes } = values;
        const metadata: PriceChangeRequestMetadata = {
            reason,
        };

        if (batchStaging) {
            batchStaging.enqueue({
                requestId: request.id,
                requestLabel: queuedRequestLabel(request),
                verb: 'decline',
                outcome: 'Declined',
                notes,
                metadata,
            });
            setSubmitting(false);
            onSuccess?.();
            return;
        }

        const ok = await submit({
            outcome: 'Declined',
            notes,
            metadata,
        });
        setSubmitting(false);
        if (ok) {
            onSuccess?.();
        }
    });

    const proceedDisabled = loading || submitting || !resolvedAction?.canSubmit;

    return (
        <form
            onSubmit={handleSubmit}
            className="flex min-h-0 min-w-0 w-full max-w-full flex-col gap-5 overflow-x-hidden px-4 py-3"
        >
            <div className="flex w-full min-w-0 max-w-full flex-col items-start gap-5">
                <Stack gap="md">
                    <Text size="md" fw={500}>
                        Decline price change
                    </Text>
                    <div className={quickActionFormFieldsShellClassName}>
                        <div
                            className={`${quickActionFormFieldsBlockClassName} ${QUICK_ACTION_APPROVE_FIELDS_WIDTH_CLASS}`}
                        >
                            <Select
                                label="Reason for declining"
                                size="sm"
                                radius={fieldRadius}
                                required
                                data={reasonData}
                                comboboxProps={{
                                    withinPortal: true,
                                    zIndex: 1000,
                                }}
                                classNames={requestComboboxSelectClassNames}
                                className="box-border w-full min-w-0"
                                {...form.getInputProps('reason')}
                            />
                            <Textarea
                                label="Notes"
                                size="sm"
                                radius={fieldRadius}
                                placeholder="Additional notes"
                                minRows={2}
                                autosize
                                className="mt-4 box-border block w-full min-w-0"
                                classNames={{
                                    root: 'box-border block w-full min-w-0',
                                    input: 'min-w-0',
                                    wrapper: 'box-border w-full',
                                }}
                                {...form.getInputProps('notes')}
                            />
                        </div>
                    </div>
                </Stack>
            </div>
            <Group gap="md" justify="flex-start" wrap="nowrap" className="pt-2">
                <Button
                    variant="outline"
                    size="md"
                    radius="xl"
                    type="button"
                    onClick={onCancel}
                    disabled={loading || submitting}
                    className={compactBtnClass}
                >
                    Cancel
                </Button>
                <Button
                    type="submit"
                    size="md"
                    radius="xl"
                    color="red"
                    loading={loading || submitting}
                    disabled={proceedDisabled}
                    className={compactBtnClass}
                >
                    Decline
                </Button>
            </Group>
        </form>
    );
}

export default QuickActionDeclineForm;
